import axios from 'axios';
import {
  invalidateAuthSession,
  isAuthSessionValid,
  touchAuthActivity,
  getResolvedAuthToken,
} from '../utils/authSession';
import { beginTrackedRequest, endTrackedRequest, isSilentRequest } from '../utils/loadingBus';

let authInterceptorRegistered = false;
let authRedirectScheduled = false;

export function getApiBaseUrl() {
    return process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
}

function isLoginOrLogoutAttempt(error) {
  const requestUrl = String(error?.config?.url || '');
  const method = String(error?.config?.method || '').toLowerCase();
  const isLoginAttempt = requestUrl.includes('/login/') && method === 'post';
  const isLogoutAttempt = requestUrl.includes('/logout/') && method === 'post';
  return { isLoginAttempt, isLogoutAttempt };
}

function redirectToLoginIfNeeded() {
  if (typeof window === 'undefined' || authRedirectScheduled) {
    return;
  }
  const path = window.location.pathname || '';
  if (path === '/' || path === '/login' || path.startsWith('/login')) {
    return;
  }
  authRedirectScheduled = true;
  window.location.replace('/');
}

function attachLoadingInterceptor(client) {
  client.interceptors.request.use((config) => {
    const method = String(config.method || 'get').toLowerCase();
    if (!isSilentRequest(config) && method !== 'get') {
      beginTrackedRequest();
      config.__trackLoading = true;
    }
    return config;
  });
  client.interceptors.response.use(
    (response) => {
      if (response?.config?.__trackLoading) {
        endTrackedRequest();
      }
      return response;
    },
    (error) => {
      if (error?.config?.__trackLoading) {
        endTrackedRequest();
      }
      return Promise.reject(error);
    },
  );
}

function attachAuthResponseInterceptor(client) {
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const { isLoginAttempt, isLogoutAttempt } = isLoginOrLogoutAttempt(error);

      if (isLogoutAttempt) {
        error.__authHandled = true;
      } else if (status === 401 && !isLoginAttempt) {
        invalidateAuthSession();
        error.__authHandled = true;
        redirectToLoginIfNeeded();
      }
      return Promise.reject(error);
    },
  );
}

export function setupAuthInterceptors() {
  if (authInterceptorRegistered) {
    return;
  }
  authInterceptorRegistered = true;
  attachLoadingInterceptor(axios);
  attachAuthResponseInterceptor(axios);
}

export function getApiClient(cookies) {
    const token = getResolvedAuthToken(cookies);
    if (token && isAuthSessionValid(token)) {
      touchAuthActivity();
    }

    const axiosClient = axios.create({
        headers: {
            'Authorization': `Token ${token}`,
            'Content-Type': "application/json",
        }
    });

    // axios.create() does not inherit default-instance interceptors
    attachLoadingInterceptor(axiosClient);
    attachAuthResponseInterceptor(axiosClient);

    return axiosClient;
}